import { FC, useContext, useEffect, useMemo, useState } from "react"
import NavShell from "../navigation/NavShell"
import UserButton from "../navigation/UserButton"
import UserButtonMobile from "../navigation/UserButtonMobile"
import useName from "../../hooks/useName"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Button } from "../ui/button"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { getUserColor } from "../../utils/getUserColor"
import { getTextColor } from "../../utils/getTextColor"
import { Link } from "react-router-dom"
import { NavLinkItem } from "../navigation/NavLinkItem"
import { NotificationCtx } from "../../context/notificationContext"
import {
  SvgCalendar,
  SvgChat,
  SvgClose,
  SvgHamburger,
  SvgHome,
  SvgUser3,
  SvgVideoOutline,
} from "../icons"
import { UserCtx } from "../../context/userContext"
import { SvgZoeeGradientFavicon } from "../icons/logos/zoee-gradient-favicon"
import { SvgZoeeGradientLogo } from "../icons/logos/zoee-gradient-logo"
import { NotificationBell } from "../navigation/notification-bell"

const MemberAuthedNav: FC = () => {
  const { user } = useContext(UserCtx)
  const { initial } = useName()
  const { previewImgUrl } = useContext(CommonFunctionCtx)
  const {
    inboxNotifications,
    setInboxNotifications,
    contactsNotifications,
    setContactsNotifications,
    notificationAlert,
    notifications,
    getNotifications,
  } = useContext(NotificationCtx)
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false)
  const [showUserButton, setShowUserButton] = useState<boolean>(false)

  const unreadInboxNotificationNumber = useMemo(() => {
    return inboxNotifications.filter((n: any) => !n.viewed).length
  }, [inboxNotifications])

  const unreadContactsNotificationNumber = useMemo(() => {
    return contactsNotifications.filter((n: any) => !n.viewed).length
  }, [contactsNotifications])

  useEffect(() => {
    // Count messages and connection request notifications
    setInboxNotifications(
      notifications.filter((n) => n.notification_category === "messages")
    )
    setContactsNotifications(
      notifications.filter((n) => {
        return n.slug === "received_connection_request" && n.viewed === 0
      })
    )
  }, [])

  useEffect(() => {
    getNotifications()
  }, [notificationAlert.show])

  return (
    <>
      <NavShell
        className="flex items-center justify-between"
        outerClassName="hidden md:flex"
      >
        <Link to="/">
          <SvgZoeeGradientFavicon />
        </Link>

        <div className="hidden items-center md:flex lg:gap-2">
          <NavLinkItem to="/member" end>
            <SvgHome />
            Home
          </NavLinkItem>
          <NavLinkItem
            to="/member/inbox"
            notifications={unreadInboxNotificationNumber}
          >
            <SvgChat />
            Inbox
          </NavLinkItem>
          <NavLinkItem
            to="/member/contacts"
            notifications={unreadContactsNotificationNumber}
          >
            <SvgUser3 />
            My Coaches
          </NavLinkItem>
          <NavLinkItem to="/member/scheduling">
            <SvgCalendar />
            My Calendar
          </NavLinkItem>
          <NavLinkItem to="/member/sessions">
            <SvgVideoOutline />
            Video Hub
          </NavLinkItem>
        </div>

        <div className="relative flex items-center gap-4">
          <NotificationBell />

          <UserButton />
        </div>
      </NavShell>

      {/* Mobile */}
      <nav className="sticky top-2.5 z-[1000] mx-2.5 mb-10 flex md:hidden">
        <div
          className="relative mx-auto flex h-20 w-full max-w-screen-xl items-center justify-between rounded-lg bg-white px-2.5 py-5 sm:px-4"
          style={{
            boxShadow:
              "0 10px 20px -5px rgba(111, 89, 246, 0.15), 0 -5px 10px -5px rgba(111, 89, 246, 0.15), 0 6px 10px -5px rgba(111, 89, 246, 0.10)",
          }}
        >
          <Button
            variant="ghost"
            className="ml-4 lg:hidden"
            size="icon"
            onClick={() => setShowMobileNav(() => !showMobileNav)}
          >
            {showMobileNav ? <SvgClose /> : <SvgHamburger />}
          </Button>
          {showMobileNav && (
            <div className="zoee-backdrop-filter fixed left-0 top-0 z-[601] h-screen w-screen"></div>
          )}

          <div
            className={`fixed bottom-0 left-0 top-0 flex min-h-screen flex-col overflow-y-auto rounded-lg bg-white p-8 transition-all duration-500 ease-in-out ${
              showMobileNav ? "left-0 w-[279px]" : "left-[-279px] w-0"
            } z-[1000]`}
            style={{
              boxShadow:
                "5px 0 15px -2px rgba(111, 89, 246, 0.15), 5px 0 15px -2px rgba(111, 89, 246, 0.15), 5px 0 15px -2px rgba(111, 89, 246, 0.1)",
            }}
            onClick={() => setShowMobileNav(false)}
          >
            <Button
              className="bg-grayFlash absolute right-[20px] top-[25px] rounded-full"
              size="icon"
              variant="ghost"
              onClick={() => setShowMobileNav(() => !showMobileNav)}
            >
              <SvgClose />
            </Button>
            <Link
              to="/"
              onClick={() => setShowMobileNav(false)}
              className="mb-[48px] mt-[10px]"
            >
              <SvgZoeeGradientLogo className="h-auto w-[155px]" />
            </Link>
            <div className="mb-[56px] flex flex-col gap-[28px] text-center text-[18px]">
              <NavLinkItem to="/member/">
                <SvgHome />
                Home
              </NavLinkItem>
              <NavLinkItem
                to="/member/inbox"
                notifications={unreadInboxNotificationNumber}
              >
                <SvgChat />
                Inbox
              </NavLinkItem>
              <NavLinkItem
                to="/member/contacts"
                notifications={unreadContactsNotificationNumber}
              >
                <SvgUser3 />
                My Coaches
              </NavLinkItem>
              <NavLinkItem to="/member/scheduling">
                <SvgCalendar />
                My Calendar
              </NavLinkItem>
              <NavLinkItem to="/member/sessions">
                <SvgVideoOutline />
                Video Hub
              </NavLinkItem>
            </div>
          </div>

          <div className="relative mr-2 flex items-center justify-end gap-4">
            <NotificationBell />

            {showUserButton ? (
              <SvgClose />
            ) : (
              <Button
                variant="ghost"
                className="h-10 w-10 rounded-full"
                onClick={() => setShowUserButton(() => !showUserButton)}
              >
                <Avatar size="xs">
                  {Boolean(user.hasAvatar) && (
                    <AvatarImage
                      alt="avatar"
                      src={previewImgUrl || user.avatar_url}
                    />
                  )}
                  <AvatarFallback className={getUserColor(user.userColor)}>
                    <div
                      style={{
                        color: getTextColor(user.userColor),
                      }}
                    >
                      {initial}
                    </div>
                  </AvatarFallback>
                </Avatar>
              </Button>
            )}
            <UserButtonMobile
              setShowUserButton={setShowUserButton}
              showUserButton={showUserButton}
            />
          </div>
        </div>
      </nav>

      <div className="hidden h-24 md:block"></div>
    </>
  )
}

export default MemberAuthedNav
